import { Redirect } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { faCoin, faMap, faSync, faUser } from '@fortawesome/pro-regular-svg-icons';
import { faRotate } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchQuickBooksSetup } from 'api/quickBooks';
import * as QuickBooks from 'assets/js/pages/account-settings/account-integrations/QuickBooks/QuickBooks';
import { useQuickBooksStatus } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/useQuickBooksStatus';
import * as GivebutterPlusSplash from 'components/givebutter-plus-splash/GivebutterPlusSplash';
import { QUICKBOOKS_HELP_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';

const STATUS_REFETCH_INTERVAL = 3500;

const QuickBooksConnect = () => {
    const { accountId } = useCurrentAccount();

    const { data: statusData } = useQuickBooksStatus({
        refetchInterval: query =>
            query.state.data?.status === null || query.state.data?.status === 'disconnected'
                ? STATUS_REFETCH_INTERVAL
                : false,
    });
    const status = statusData.status;

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.quickBooksSetup(accountId),
        queryFn: () => fetchQuickBooksSetup(accountId),
    });
    const { auth_url: authURL } = data;

    if (status === 'connected') {
        return (
            <Redirect to={`/accounts/${accountId}/settings/integrations/quickbooks/configure`} />
        );
    }

    return (
        <QuickBooks.Root>
            <QuickBooks.Header>
                <div className="flex w-full items-center justify-between gap-4">
                    <img
                        alt="QuickBooks"
                        className="max-h-8"
                        src={`/images/logos/quickbooks_combologo.svg`}
                    />

                    <Button
                        variant="solid"
                        onClick={() => authURL && openAuthWindow(authURL)}
                        size="sm"
                    >
                        <FontAwesomeIcon icon={faRotate} className="!text-white" />
                        Connect
                    </Button>
                </div>
            </QuickBooks.Header>
            <div>
                <p>
                    Keep your books balanced effortlessly. Automatically sync donations, refunds,
                    and donor information between Givebutter and QuickBooks to save hours of manual
                    work.
                </p>
                <p>
                    <a href={QUICKBOOKS_HELP_LINK} target="_blank" rel="noreferrer">
                        Read the integration guide
                    </a>
                </p>
            </div>

            <Separator />

            <GivebutterPlusSplash.FeatureCardRow>
                <GivebutterPlusSplash.FeatureCard>
                    <GivebutterPlusSplash.FeatureCardIcon className="bg-gbp-gradient-1">
                        <FontAwesomeIcon icon={faSync} size="xl" />
                    </GivebutterPlusSplash.FeatureCardIcon>
                    <GivebutterPlusSplash.FeatureCardText>
                        <GivebutterPlusSplash.FeatureCardTextHeading>
                            Hands-free daily syncing
                        </GivebutterPlusSplash.FeatureCardTextHeading>
                        <GivebutterPlusSplash.FeatureCardTextDescription>
                            Automatically transfer donation transactions from Givebutter to
                            QuickBooks, eliminating manual data entry and reducing accounting
                            errors.
                        </GivebutterPlusSplash.FeatureCardTextDescription>
                    </GivebutterPlusSplash.FeatureCardText>
                </GivebutterPlusSplash.FeatureCard>

                <GivebutterPlusSplash.FeatureCard>
                    <GivebutterPlusSplash.FeatureCardIcon className="bg-gbp-gradient-2">
                        <FontAwesomeIcon icon={faCoin} size="xl" />
                    </GivebutterPlusSplash.FeatureCardIcon>
                    <GivebutterPlusSplash.FeatureCardText>
                        <GivebutterPlusSplash.FeatureCardTextHeading>
                            Accurate, audit-ready records
                        </GivebutterPlusSplash.FeatureCardTextHeading>
                        <GivebutterPlusSplash.FeatureCardTextDescription>
                            Transactions land as balanced sales receipts (with fees separated),
                            reducing errors, speeding up month-end close, and giving your team
                            real-time financial insight.
                        </GivebutterPlusSplash.FeatureCardTextDescription>
                    </GivebutterPlusSplash.FeatureCardText>
                </GivebutterPlusSplash.FeatureCard>
            </GivebutterPlusSplash.FeatureCardRow>
            <GivebutterPlusSplash.FeatureCardRow>
                <GivebutterPlusSplash.FeatureCard>
                    <GivebutterPlusSplash.FeatureCardIcon className="bg-gbp-gradient-1">
                        <FontAwesomeIcon icon={faMap} size="xl" />
                    </GivebutterPlusSplash.FeatureCardIcon>
                    <GivebutterPlusSplash.FeatureCardText>
                        <GivebutterPlusSplash.FeatureCardTextHeading>
                            Customizable Transaction Mapping
                        </GivebutterPlusSplash.FeatureCardTextHeading>
                        <GivebutterPlusSplash.FeatureCardTextDescription>
                            Configure how donations appear in QuickBooks with flexible options for
                            service items, classes, and account assignments to match your
                            organization's chart of accounts.
                        </GivebutterPlusSplash.FeatureCardTextDescription>
                    </GivebutterPlusSplash.FeatureCardText>
                </GivebutterPlusSplash.FeatureCard>

                <GivebutterPlusSplash.FeatureCard>
                    <GivebutterPlusSplash.FeatureCardIcon className="bg-gbp-gradient-2">
                        <FontAwesomeIcon icon={faUser} size="xl" />
                    </GivebutterPlusSplash.FeatureCardIcon>
                    <GivebutterPlusSplash.FeatureCardText>
                        <GivebutterPlusSplash.FeatureCardTextHeading>
                            Donor Information Integration
                        </GivebutterPlusSplash.FeatureCardTextHeading>
                        <GivebutterPlusSplash.FeatureCardTextDescription>
                            Seamlessly transfer donor contact details and transaction histories to
                            maintain comprehensive records across both platforms for better donor
                            management and tax reporting.
                        </GivebutterPlusSplash.FeatureCardTextDescription>
                    </GivebutterPlusSplash.FeatureCardText>
                </GivebutterPlusSplash.FeatureCard>
            </GivebutterPlusSplash.FeatureCardRow>
        </QuickBooks.Root>
    );
};

const openAuthWindow = (url: string) => {
    const parameters =
        'location=1,width=800,height=650,left=' +
        (screen.width - 800) / 2 +
        ',top=' +
        (screen.height - 650) / 2;
    window.open(url, 'quickBooksConnectPopup', parameters);
};

export { QuickBooksConnect, openAuthWindow };
